import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from 'react'
import type { Ref } from 'react'
import { Upload, TriangleAlert, X } from 'lucide-react'
import classNames from 'classnames'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Label from '@/elements/Label'
import Text from '@/elements/Text'
import { colors } from '@/utils/theme'
import {
  rejectInput,
  validateDuplicates,
  validateImageResolution,
  validateMaxFiles,
  validateMaxSize,
  validateFileTypes,
  type FileValidationResult
} from '@/utils/fileValidator'

import './FormFile.scss'

interface FormFileProps {
  className?: string
  name: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onClear?: (files: File[]) => void
  label?: string
  placeholder?: string
  triggerVariant?: 'primary' | 'secondary' | 'accent'
  files: File[]
  accept?: string
  multiple?: boolean
  maxFiles?: number
  maxFileSize?: number // Max file size in MB
  error?: string
  instructions?: string
  previousImageUrl?: string
  preview?: 'default' | 'avatar' | null
  scale?: string
  disabled?: boolean
  preventDuplicate?: boolean
  minImageHeight?: number // Minimum image height in px
  minImageWidth?: number // Minimum image width in px
}

function FormFile({
  className,
  name,
  onChange,
  onBlur,
  onClear,
  label,
  placeholder = 'Select files',
  triggerVariant = 'secondary',
  files,
  accept,
  multiple,
  maxFiles,
  maxFileSize,
  error,
  instructions,
  previousImageUrl,
  preview,
  scale,
  disabled,
  preventDuplicate,
  minImageHeight,
  minImageWidth,
}: FormFileProps, ref: Ref<HTMLInputElement>) {
  const inputRef = useRef<HTMLInputElement>(null!)

  useImperativeHandle(ref, () => inputRef.current)

  const handleBrowseFiles = () => {
    inputRef.current?.click()
  }

  const filesToDisplay = useMemo(() => {
    if (Array.isArray(files)) {
      return files
    }
    if (files) {
      return [files]
    }
    return []
  }, [files])

  const shortenFileName = (name: string) => {
    if (name.length > 30) {
      return `${name.slice(0, 27)}...`
    }
    return name
  }

  const imageUrl = useMemo(() => {
    if (multiple) return null
    if (filesToDisplay.length > 0) {
      return URL.createObjectURL(filesToDisplay[0])
    }
    return previousImageUrl || ''
  }, [filesToDisplay, multiple, previousImageUrl])

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])

    const validators = [
      accept && (() => validateFileTypes(selected, accept)),
      preventDuplicate && (() => validateDuplicates(selected, filesToDisplay)),
      maxFileSize && (() => validateMaxSize(selected, maxFileSize)),
      maxFiles && (() => validateMaxFiles(selected, filesToDisplay.length, maxFiles)),
      (minImageHeight || minImageWidth) &&
      (() => validateImageResolution(selected, minImageWidth, minImageHeight))
    ].filter(Boolean) as (() => Promise<FileValidationResult> | FileValidationResult)[]

    for (const validate of validators) {
      const result = await validate()
      if (!result.valid) {
        return rejectInput(e, result.message!)
      }
    }

    onChange?.(e)
    return null
  }, [accept, preventDuplicate, maxFileSize, maxFiles, minImageHeight, minImageWidth, onChange, filesToDisplay])

  const ImagePreview = useCallback(() => {
    const wrapperStyles: React.CSSProperties = {
      ...preview === 'default' ? {
        width: '100%',
        height: !scale ? 148 : 'auto',
        aspectRatio: scale ? scale.replace(':', '/') : undefined,
        borderRadius: 8,
      } : {
        minWidth: 83,
        maxWidth: 83,
        minHeight: 83,
        maxHeight: 83,
        borderRadius: '50%',
        justifyContent: 'center',
      },
      overflow: 'hidden',
      backgroundColor: colors['gray-light-40'],
    }

    return (
      <Flex
        wfull
        justify='center'
        align='center'
        style={wrapperStyles}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt='Image Preview'
            style={{
              width: preview === 'avatar' ? 83 : '100%',
              height: preview === 'avatar' ? 83 : 'auto',
              objectFit: 'cover',
            }}
          />
        )}
      </Flex>
    )
  }, [imageUrl, preview, scale])

  return (
    <div className={classNames('form-field', className)}>
      <Flex pb={1}><Label htmlFor={name} hidden={!label}>{label}</Label></Flex>
      <Flex gap={3} inline>
        {preview === 'avatar' && <ImagePreview />}
        <Flex wfull gap={2}>
          {(preview === 'default' && imageUrl) && <ImagePreview />}
          <Flex
            inline
            align='center'
            gh={3}
            gv={2}
            pt={preview === 'default' && imageUrl ? 1 : 'unset'}
            wrap
          >
            <>
              <Button
                id={name}
                variant={triggerVariant}
                onClick={handleBrowseFiles}
                disabled={disabled}
              >
                <Upload size={18} /> {placeholder}
              </Button>
              <input
                type='file'
                className='file-input'
                name={name}
                onChange={handleFileChange}
                onBlur={onBlur}
                ref={inputRef}
                multiple={multiple}
                accept={accept}
                disabled={disabled}
              />
            </>
            {(imageUrl && !disabled) && (
              <Button
                variant='textOnlyDanger'
                onClick={() => {
                  onClear?.([])
                  if (inputRef.current) {
                    inputRef.current.value = ''
                  }
                  URL.revokeObjectURL(imageUrl)
                }}
              >
                Remove Photo
              </Button>
            )}
          </Flex>
          {!preview && filesToDisplay && (
            <div className='file-tags'>
              {filesToDisplay.map((file, idx) => (
                <div key={file.name} className='file-tag'>
                  {shortenFileName(file.name)}
                  <X
                    className='close'
                    size={20}
                    onClick={() => {
                      const newFiles = filesToDisplay.filter((_, i) => i !== idx)
                      onClear?.(newFiles)
                      if (inputRef.current) {
                        inputRef.current.value = ''
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          {instructions && (
            <Text variant='textblock' color='black-light-40'>
              {instructions}
            </Text>
          )}
          <Flex>
            {error && (
              <span className='field-error'>
                <TriangleAlert size={12} />
                {error}
              </span>
            )}
          </Flex>
        </Flex>
      </Flex>
    </div>
  )
}

export default forwardRef(FormFile)
